import {tokensForThemeProvider} from '@forecast-it/design-system';
import React from 'react';
import styled from 'styled-components';

export type NovaCompactButtonTone = 'accent' | 'neutral';

export interface NovaCompactButtonProps {
	onPress: () => void;
	tone?: NovaCompactButtonTone;
	children: React.ReactNode;
	disabled?: boolean;
	// Why the control is refused, when it is. Rendered as the native title so the reason is legible
	// on hover to everybody, the same way the row control states its own.
	title?: string;
	// Passed through because a caller may need to point something at this button - the agent
	// picker positions itself against its own trigger by element id.
	id?: string;
	'data-cy'?: string;
}

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

const SURFACE = sys?.background.neutral['1'] ?? '';
const ACCENT_TEXT = sys?.text.content.primary ?? '';
const ACCENT_BORDER = ref?.blue['100'] ?? '';
const NEUTRAL_TEXT = sys?.text.content.auxiliary ?? '';
const NEUTRAL_BORDER = sys?.border.decorative ?? '';
const DISABLED_TEXT = sys?.text.disabled.ondisabled ?? '';
const RADIUS = size?.borderradius['3'] ?? '';
const FOCUS_RING = sys?.border.focusring ?? '';

// The actions that sit inside a Nova answer card: Copy, Discard, and whatever a later turn adds
// beside them. They are deliberately smaller than a design-system Button, which is 44px tall and
// carries its own emphasis palette - three of those in a card would out-weigh the answer they act
// on. Everything here is a design-system token; only the proportions are this component's own.
const CompactButton = styled.button<{tone: NovaCompactButtonTone}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 14px;
	background: ${SURFACE};
	border: 1px solid ${props => (props.tone === 'accent' ? ACCENT_BORDER : NEUTRAL_BORDER)};
	border-radius: ${RADIUS};
	color: ${props => (props.tone === 'accent' ? ACCENT_TEXT : NEUTRAL_TEXT)};
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	cursor: pointer;

	&:disabled {
		color: ${DISABLED_TEXT};
		border-color: ${NEUTRAL_BORDER};
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid ${FOCUS_RING};
		outline-offset: 1px;
	}
`;

export const NovaCompactButton = (props: NovaCompactButtonProps): JSX.Element => (
	<CompactButton
		type="button"
		tone={props.tone ?? 'accent'}
		disabled={props.disabled}
		title={props.title}
		onClick={props.onPress}
		id={props.id}
		data-cy={props['data-cy']}
	>
		{props.children}
	</CompactButton>
);
